import { useState } from "react";
import type { FormEvent } from "react";
import { Button, Field, Input, errorMessage, format } from "@fortex/map";
import { PageFrame } from "../app/App";
import { useApp, useAuth } from "../app/context";

export function passwordProblems(labels: { passwordMinLength: string; passwordMismatch: string }, next: string, confirm: string): string | null {
  if (next.length < 6) return labels.passwordMinLength;
  if (next !== confirm) return labels.passwordMismatch;

  return null;
}

export function AccountPage() {
  const { api, labels, toast } = useApp();
  const user = useAuth(a => a.user);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const problem = touched ? passwordProblems(labels, next, confirm) : null;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);

    if (current.length === 0 || passwordProblems(labels, next, confirm) !== null) return;

    setBusy(true);
    setError(null);

    try {
      await api.changePassword(current, next, confirm);
      toast("success", labels.passwordChanged);
      setCurrent("");
      setNext("");
      setConfirm("");
      setTouched(false);
    } catch (reason) {
      setError(errorMessage(reason, labels));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageFrame title={labels.changePassword} icon="save" narrow>
      <form className="fxapp-card fxapp-form" onSubmit={submit} noValidate>
        <h1 className="fxmap-display fxapp-title">{labels.changePassword}</h1>
        {user !== null ? <p className="fxmap-secondary">{format(labels.signedInAs, { name: user.fullName || user.email || user.name || "" })}</p> : null}
        <Field label={labels.currentPassword} required error={touched && current.length === 0 ? labels.required : null}>
          <Input type="password" autoComplete="current-password" value={current} onChange={event => setCurrent(event.target.value)} />
        </Field>
        <Field label={labels.newPassword} required error={problem === labels.passwordMinLength ? problem : null}>
          <Input type="password" autoComplete="new-password" value={next} onChange={event => setNext(event.target.value)} />
        </Field>
        <Field label={labels.confirmPassword} required error={problem === labels.passwordMismatch ? problem : null}>
          <Input type="password" autoComplete="new-password" value={confirm} onChange={event => setConfirm(event.target.value)} />
        </Field>
        {error !== null ? (
          <p className="fxapp-form-error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="fxapp-form-actions">
          <Button kind="primary" type="submit" label={labels.save} busy={busy} />
        </div>
      </form>
    </PageFrame>
  );
}
